import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, withRouter } from 'react-router';
import { Button } from 'antd';
import RouteWithSubRoutes from '../route_with_sub_routes/RouteWithSubRoutes';
import { OWSubTabs } from '../ow_sub_tabs/OWSubTabs';
import { AdvancedFilters } from '../advanced_filters/AdvancedFilters';
import SelectedFilters from '../selected_filters_pills/SelectedFilters';
import AddComplianceDocumentModal from './AddComplianceDocumentModal';
import { COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS } from '../../actions/compliance_request_instances_actions';
import { COMPLIANCE_DOCUMENTS_CRUD_ACTION_CREATORS } from '../../actions/compliance_documents_actions';
import { fetchComplianceSummary } from '../../thunks/compliance_request_instances_thunks';
import { complianceDocumentsRestCrudThunks } from '../../thunks/compliance_documents_thunks';
import { complianceTypesRestCrudThunks } from '../../thunks/compliance_types_thunks';
import { fetchComplianceClientStatuses } from '../../thunks/compliance_statuses_thunks';
import posthog from 'posthog-js';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	buildSupplierComplianceOverviewFilterConfig,
	buildSupplierComplianceRequestsFilterConfig,
	buildSupplierComplianceDocumentsFilterConfig,
} from '../../utils/ComplianceUtils';
import { COMPLIANCE_FEATURE_FLAG } from '../compliance_common/ComplianceConstants';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';

// Third-party suppliers get the document-centric supplier view (Overview /
// Documents / Requests, "+ Add Document"). Internal-tech suppliers act as the
// buyer and render the buyer experience directly (BuyerCompliancePage, via the
// /supplier/suppliers/overview/compliance route) — this shell is third-party only.
const THIRD_PARTY_SUB_TABS = [
	{ key: 'overview', name: 'Overview' },
	{ key: 'documents', name: 'Documents' },
	{ key: 'requests', name: 'Requests' },
];

// The compliance suite mounts under a couple of parents (the third-party "Routing"
// and "Clients" areas), so the base path is derived from the current URL (everything
// up to and including '/compliance') rather than hardcoded. This default is only a
// fallback if the marker isn't found.
const DEFAULT_BASE_PATH = '/supplier/clients/overview/compliance';
const COMPLIANCE_PATH_MARKER = '/compliance';
// Overview target collection whose redux filters hold the search + compliance values.
const THIRD_PARTY_OVERVIEW_TARGET_COLLECTION = 'supplierComplianceOverview';
// The Requests tab reads this collection (see SupplierComplianceRequestsPage); the
// shell's Requests-tab AdvancedFilters/pills write Client + Due-date + search here.
const THIRD_PARTY_REQUESTS_TARGET_COLLECTION = 'supplierComplianceRequestsList';
// compliance_documents collection backing the Overview "Document" async-select filter
// (seeded in compliance_documents_reducer).
const DOCUMENTS_FILTER_TARGET_COLLECTION = 'complianceOverviewDocumentFilter';
// The Documents tab reads this collection (see SupplierComplianceDocumentsPage); the
// shell's Documents-tab AdvancedFilters/pills write Compliance-type + Expiry-date +
// search here, on the compliance_documents slice.
const DOCUMENTS_INDEX_TARGET_COLLECTION = 'supplierComplianceDocumentsIndex';
// compliance_types collection backing the Documents-tab "Compliance type" async-select
// filter (seeded in compliance_types_reducer).
const DOCUMENTS_TYPE_FILTER_TARGET_COLLECTION = 'supplierComplianceDocumentsTypeFilter';

const SupplierCompliancePage: FC<any> = ({
	history,
	childRoutes,
	summary,
	complianceRequestInstances,
	complianceDocuments,
	complianceTypes,
	documentsCount,
	fetchSummary,
	fetchClientStatuses,
	fetchDocumentOptions,
	fetchDocumentTypeOptions,
	refetchComplianceDocuments,
	updateInstanceFilters,
	clearAndUpdateInstanceFilters,
	updateDocumentFilters,
	clearAndUpdateDocumentFilters,
}): React.ReactElement => {
	const pathname = history.location.pathname;
	const basePath = useMemo(() => {
		const markerIndex = pathname.indexOf(COMPLIANCE_PATH_MARKER);
		return markerIndex === -1
			? DEFAULT_BASE_PATH
			: pathname.slice(0, markerIndex + COMPLIANCE_PATH_MARKER.length);
	}, [pathname]);
	const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
	// The supplier's clients ({ value: buyerCompanyId, label: name }) backing the
	// Client static-select on the Overview + Requests filters. Sourced from
	// /compliance/client_statuses (there is no /compliance/clients endpoint).
	const [clientItems, setClientItems] = useState([]);

	useMountEffect(() => {
		fetchSummary();
		// Load the supplier's clients for the Client filter from client_statuses.
		fetchClientStatuses().then((statuses) =>
			setClientItems(
				(statuses || []).map((status) => ({
					value: status.buyerCompanyId,
					label: status.buyerCompanyName,
				}))
			)
		);
		// Prime the Document async-select collection: OWAsyncSelect renders DISABLED
		// while its option list is empty (see its `noVisibleRecordsWithoutSearch`
		// guard), so load it before the Filters popover first opens.
		fetchDocumentOptions({}, DOCUMENTS_FILTER_TARGET_COLLECTION, { current: 1, pageSize: 25 });
		// Same priming for the Documents-tab "Compliance type" async-select filter.
		fetchDocumentTypeOptions({}, DOCUMENTS_TYPE_FILTER_TARGET_COLLECTION, {
			current: 1,
			pageSize: 25,
		});
	});

	const openAddDocumentModal = () => setAddDocumentModalOpen(true);
	const closeAddDocumentModal = () => setAddDocumentModalOpen(false);
	const handleAddDocumentSuccess = () => {
		setAddDocumentModalOpen(false);
		refetchComplianceDocuments();
		fetchSummary();
	};

	// Overview: server-side Client + Document + Due-date filters (all dimensions the
	// /request_instances list accepts). They write into the overview redux filters,
	// which the overview forwards as query params. Search is AdvancedFilters' built-in
	// input. Memoized because it feeds both AdvancedFilters and SelectedFilters (whose
	// effects key on config identity).
	const thirdPartyFilterConfig = useMemo(
		() =>
			buildSupplierComplianceOverviewFilterConfig(
				THIRD_PARTY_OVERVIEW_TARGET_COLLECTION,
				updateInstanceFilters,
				clientItems,
				complianceDocuments,
				fetchDocumentOptions,
				DOCUMENTS_FILTER_TARGET_COLLECTION
			),
		[updateInstanceFilters, clientItems, complianceDocuments, fetchDocumentOptions]
	);
	// Requests tab: Client + Due-date server-side filters + built-in search.
	const thirdPartyRequestsFilterConfig = useMemo(
		() =>
			buildSupplierComplianceRequestsFilterConfig(
				THIRD_PARTY_REQUESTS_TARGET_COLLECTION,
				updateInstanceFilters,
				clientItems
			),
		[updateInstanceFilters, clientItems]
	);
	// Documents tab: Compliance-type + Expiry-date server-side filters + built-in
	// search. Unlike the other two tabs these write to the compliance_documents slice
	// (via updateDocumentFilters), which the Documents PaginatedReduxTable reads.
	const thirdPartyDocumentsFilterConfig = useMemo(
		() =>
			buildSupplierComplianceDocumentsFilterConfig(
				DOCUMENTS_INDEX_TARGET_COLLECTION,
				updateDocumentFilters,
				complianceTypes,
				fetchDocumentTypeOptions,
				DOCUMENTS_TYPE_FILTER_TARGET_COLLECTION
			),
		[updateDocumentFilters, complianceTypes, fetchDocumentTypeOptions]
	);

	const checkSubTabActive = (key: string) => pathname.indexOf(`${basePath}/${key}`) !== -1;
	const handleSubTabChange = useCallback(
		(key) => history.push(`${basePath}/${key}`),
		[history, basePath]
	);
	const renderBaseRedirect = () => <Redirect to={{ pathname: `${basePath}/overview` }} />;

	const nonCompliantClients = nullSafeGet('nonCompliantClients', summary);
	const totalClients = nullSafeGet('totalClients', summary);

	const nonCompliantBanner = totalClients ? (
		<span className="bodyregular">
			Non-Compliant with{' '}
			<span className="bodymedium underline" style={{ color: 'rgb(204, 50, 50)' }}>{`${
				nonCompliantClients || 0
			}/${totalClients}`}</span>{' '}
			clients
		</span>
	) : null;

	const activeSubTab = checkSubTabActive('documents')
		? 'documents'
		: checkSubTabActive('requests')
		? 'requests'
		: 'overview';
	// All three tabs carry server-side filters + built-in search. Overview + Requests
	// write to the instances slice; Documents writes to the compliance_documents slice
	// — so the active slice + update handlers switch too.
	const isRequestsSubTab = activeSubTab === 'requests';
	const isDocumentsSubTab = activeSubTab === 'documents';
	const activeFilterTarget = isDocumentsSubTab
		? DOCUMENTS_INDEX_TARGET_COLLECTION
		: isRequestsSubTab
		? THIRD_PARTY_REQUESTS_TARGET_COLLECTION
		: THIRD_PARTY_OVERVIEW_TARGET_COLLECTION;
	const activeFilterConfig = isDocumentsSubTab
		? thirdPartyDocumentsFilterConfig
		: isRequestsSubTab
		? thirdPartyRequestsFilterConfig
		: thirdPartyFilterConfig;
	const activeFilterSlice = isDocumentsSubTab ? complianceDocuments : complianceRequestInstances;
	const activeUpdateFilters = isDocumentsSubTab ? updateDocumentFilters : updateInstanceFilters;
	const activeClearAndUpdateFilters = isDocumentsSubTab
		? clearAndUpdateDocumentFilters
		: clearAndUpdateInstanceFilters;
	// "Documents (N)" — server total for the documents index, when known.
	const subTabs = THIRD_PARTY_SUB_TABS.map((tab) => {
		if (tab.key === 'documents' && documentsCount !== null && documentsCount !== undefined) {
			return { key: tab.key, label: `${tab.name} (${documentsCount})` };
		}
		return { key: tab.key, label: tab.name };
	});

	// Feature-flag gate (defense-in-depth for direct URLs — the Clients Compliance tab
	// is already hidden when off). basePath ends in '/compliance'; drop it to land on
	// the parent overview, which redirects to its default tab.
	if (!posthog.isFeatureEnabled(COMPLIANCE_FEATURE_FLAG)) {
		return <Redirect to={{ pathname: basePath.replace(COMPLIANCE_PATH_MARKER, '') }} />;
	}

	return (
		<div className="supplier-compliance-page">
			<Route path={basePath} exact={true} render={renderBaseRedirect} />
			<div className="flex flex-row flex-wrap items-center gap-4 px-6 py-4">
				<OWSubTabs tabs={subTabs} activeKey={activeSubTab} onChange={handleSubTabChange} />
				<AdvancedFilters
					key={activeFilterTarget}
					filtersTargetCollectionName={activeFilterTarget}
					filtersStateSlice={activeFilterSlice}
					updateFilters={activeUpdateFilters}
					clearAndUpdateFilters={activeClearAndUpdateFilters}
					filterConfig={activeFilterConfig}
					filterPlaceHolder="Search"
				/>
				<div className="ml-auto flex flex-row items-center gap-3">
					{nonCompliantBanner}
					<Button type="primary" onClick={openAddDocumentModal}>
						+ Add Document
					</Button>
				</div>
			</div>
			{/* Removable filter pills. `containerClassName` puts the spacing + horizontal
			    padding on SelectedFilters' own root, which renders only when pills exist —
			    so there's no phantom gap when no filters are set. */}
			<SelectedFilters
				containerClassName="mb-3 px-6"
				filterConfig={activeFilterConfig}
				filterValues={nullSafeGetOrElse(`${activeFilterTarget}.filters`, activeFilterSlice, {})}
				updateFilters={(patch) => activeUpdateFilters(patch, activeFilterTarget)}
			/>
			{addDocumentModalOpen && (
				<AddComplianceDocumentModal
					visible={addDocumentModalOpen}
					onCancel={closeAddDocumentModal}
					onSuccess={handleAddDocumentSuccess}
				/>
			)}
			{childRoutes.map((route, index) => (
				<RouteWithSubRoutes key={index} {...route} />
			))}
		</div>
	);
};

const mapStateToProps = (state) => ({
	summary: nullSafeGetOrElse('counts.complianceSummary', state.compliance_request_instances, null),
	complianceRequestInstances: state.compliance_request_instances,
	// Backs the Overview Client + Document filters + the Documents-tab index + its
	// filters (all on the compliance_documents slice).
	complianceDocuments: state.compliance_documents,
	// Backs the Documents-tab "Compliance type" async-select filter.
	complianceTypes: state.compliance_types,
	// Server total for the Documents index -> the "Documents (N)" tab count.
	documentsCount: nullSafeGetOrElse(
		'supplierComplianceDocumentsIndex.pagination.total',
		state.compliance_documents,
		null
	),
});

const mapDispatchToProps = (dispatch) => ({
	fetchSummary: () => dispatch(fetchComplianceSummary(ROLE_TYPES.SUPPLIER)()),
	// Client filter options — the supplier's connected buyer companies, from
	// /compliance/client_statuses ({ buyerCompanyId, buyerCompanyName, ... }).
	fetchClientStatuses: () => dispatch(fetchComplianceClientStatuses(ROLE_TYPES.SUPPLIER)()),
	// Overview Document filter — plain /documents read backing the async select (id +
	// name). Also primes the collection so the select isn't disabled.
	fetchDocumentOptions: (params, targetCollectionName, pagination, sorting, filters, add) =>
		dispatch(
			complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).read(
				params,
				targetCollectionName || DOCUMENTS_FILTER_TARGET_COLLECTION,
				pagination,
				sorting,
				filters,
				add
			)
		),
	// Documents-tab "Compliance type" async-select options — plain /types read backing
	// the select (id + name). Also primes the collection so the select isn't disabled.
	fetchDocumentTypeOptions: (params, targetCollectionName, pagination, sorting, filters, add) =>
		dispatch(
			complianceTypesRestCrudThunks(ROLE_TYPES.SUPPLIER).read(
				params,
				targetCollectionName || DOCUMENTS_TYPE_FILTER_TARGET_COLLECTION,
				pagination,
				sorting,
				filters,
				add
			)
		),
	// Overview/Requests filters (Client + Due-date + search) persist in the target
	// collection's redux filters, which the overview reads and forwards.
	updateInstanceFilters: (filters, targetCollectionName) =>
		dispatch(
			COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)
		),
	clearAndUpdateInstanceFilters: (filters, targetCollectionName) =>
		dispatch(
			COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
	// Documents-tab filters (Compliance type + Expiry range + search) persist in the
	// compliance_documents index collection's redux filters, which the Documents
	// PaginatedReduxTable forwards to the server as query params.
	updateDocumentFilters: (filters, targetCollectionName) =>
		dispatch(
			COMPLIANCE_DOCUMENTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)
		),
	clearAndUpdateDocumentFilters: (filters, targetCollectionName) =>
		dispatch(
			COMPLIANCE_DOCUMENTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	// Replay the documents collection's current filters + pagination (read at call
	// time via getState) so adding a document keeps the Documents tab on its current
	// filtered page instead of snapping back to unfiltered page 1.
	refetchComplianceDocuments: () =>
		dispatch((innerDispatch, getState) => {
			const slice = getState().compliance_documents;
			const filters = nullSafeGetOrElse('supplierComplianceDocumentsIndex.filters', slice, {});
			const pagination = nullSafeGetOrElse('supplierComplianceDocumentsIndex.pagination', slice, {
				current: 1,
				pageSize: 10,
			});
			return innerDispatch(
				complianceDocumentsRestCrudThunks(ROLE_TYPES.SUPPLIER).read(
					{},
					'supplierComplianceDocumentsIndex',
					pagination,
					null,
					filters
				)
			);
		}),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(SupplierCompliancePage)
);
